import type {
  PublicLookupData,
  PublicRegistrationRequest,
  RegistrationSummary,
  SubjectAgeBand,
  TagType,
} from "@/lib/api/client"
import {
  getLookupCompliance,
  isGuardianRequiredRegistration,
} from "@/features/public/register/registration-compliance"
import { getPlantationLocalLanguageOption } from "@/lib/safetyqr-reporting-templates"

export interface RegistrationFormState {
  allergies: string
  bloodType: string
  color: string
  companyName: string
  consentAccepted: boolean
  deviceTag: string
  emergencyContactEmail: string
  emergencyContactName: string
  emergencyContactPhone: string
  fleetName: string
  guardianEmail: string
  guardianName: string
  guardianPhone: string
  guardianRelationship: string
  make: string
  medicalConditions: string
  medications: string
  noticeVersionId: string
  model: string
  ownerEmail: string
  ownerName: string
  ownerPhone: string
  plantationId: string
  preferredLanguageCode: string
  preferredLanguageLabel: string
  plantationName: string
  plateNumber: string
  responderNotes: string
  registrantRelationship: string
  subjectAgeBand: SubjectAgeBand
  tagType: TagType
  vehicleType: string
  vin: string
  year: string
  zoneName: string
}

export function createInitialRegistrationFormState(
  initialTag = "",
  tagType: TagType = "vehicle_sticker"
): RegistrationFormState {
  return {
    allergies: "",
    bloodType: "",
    color: "",
    companyName: "",
    consentAccepted: false,
    deviceTag: initialTag,
    emergencyContactEmail: "",
    emergencyContactName: "",
    emergencyContactPhone: "",
    fleetName: "",
    guardianEmail: "",
    guardianName: "",
    guardianPhone: "",
    guardianRelationship: "",
    make: "",
    medicalConditions: "",
    medications: "",
    noticeVersionId: "",
    model: "",
    ownerEmail: "",
    ownerName: "",
    ownerPhone: "",
    plantationId: "",
    preferredLanguageCode: "",
    preferredLanguageLabel: "",
    plantationName: "",
    plateNumber: "",
    responderNotes: "",
    registrantRelationship: "self",
    subjectAgeBand: "adult_18_plus",
    tagType,
    vehicleType: "",
    vin: "",
    year: "",
    zoneName: "",
  }
}

export function mergeLookupIntoRegistrationForm(
  form: RegistrationFormState,
  lookup: PublicLookupData
): RegistrationFormState {
  const registration = lookup.registration
  const contact = lookup.contact
  const assignment = lookup.assignment
  const medicalProfile = lookup.medicalProfile
  const vehicle = lookup.vehicle
  const tagType = lookup.tagType ?? form.tagType
  const isPlantations = isPlantationsLookup(lookup)
  const isPersonal = tagType === "personal_tag"
  const isFleet = tagType === "fleet_tag"
  const compliance = getLookupCompliance(lookup)
  const subjectAgeBand =
    compliance?.subjectAgeBand === "under_18" ? "under_18" : form.subjectAgeBand

  return {
    ...form,
    allergies: firstDefined(form.allergies, medicalProfile?.allergies, registration?.allergies),
    bloodType: firstDefined(form.bloodType, medicalProfile?.bloodType, registration?.bloodType),
    color: firstDefined(form.color, vehicle?.color, registration?.vehicleColor),
    companyName: firstDefined(form.companyName, registration?.companyName),
    consentAccepted: Boolean(
      form.consentAccepted
      || medicalProfile?.consentAccepted
      || registration?.consentAccepted
    ),
    deviceTag: lookup.tag,
    emergencyContactName: firstDefined(
      form.emergencyContactName,
      isPlantations ? registration?.emergencyContactName : "",
      contact?.emergencyContactName,
      registration?.emergencyContactName
    ),
    emergencyContactEmail: firstDefined(
      form.emergencyContactEmail,
      registration?.emergencyContactEmail
    ),
    emergencyContactPhone: firstDefined(
      form.emergencyContactPhone,
      isPlantations ? registration?.emergencyContactPhone : "",
      contact?.emergencyContactPhone,
      registration?.emergencyContactPhone
    ),
    fleetName: firstDefined(form.fleetName, registration?.fleetName),
    guardianEmail: firstDefined(form.guardianEmail, compliance?.guardianEmail),
    guardianName: firstDefined(form.guardianName, compliance?.guardianName),
    guardianPhone: firstDefined(form.guardianPhone, compliance?.guardianPhone),
    guardianRelationship: firstDefined(
      form.guardianRelationship,
      compliance?.guardianRelationship
    ),
    make: firstDefined(form.make, vehicle?.make, registration?.vehicleMake),
    medicalConditions: firstDefined(
      form.medicalConditions,
      medicalProfile?.medicalConditions,
      registration?.medicalConditions
    ),
    medications: firstDefined(
      form.medications,
      medicalProfile?.medications,
      registration?.medications
    ),
    noticeVersionId: firstDefined(
      form.noticeVersionId,
      compliance?.activeNoticeVersionId
    ),
    model: firstDefined(form.model, vehicle?.model, registration?.vehicleModel),
    ownerEmail: firstDefined(
      form.ownerEmail,
      isPlantations ? registration?.ownerEmail : "",
      isPersonal ? registration?.email : "",
      isFleet ? registration?.contactEmail : "",
      isFleet ? assignment?.assigneeEmail : "",
      registration?.ownerEmail,
      contact?.email
    ),
    ownerName: firstDefined(
      form.ownerName,
      isPlantations ? registration?.ownerName : "",
      isPersonal ? registration?.fullName : "",
      isFleet ? registration?.contactName : "",
      isFleet ? assignment?.assigneeName : "",
      registration?.ownerName,
      contact?.name
    ),
    ownerPhone: firstDefined(
      form.ownerPhone,
      isPlantations ? registration?.ownerPhone : "",
      isPersonal ? registration?.phone : "",
      isFleet ? registration?.contactPhone : "",
      isFleet ? assignment?.assigneePhone : "",
      registration?.ownerPhone,
      contact?.phone
    ),
    plantationId: firstDefined(
      form.plantationId,
      registration?.plantationId,
      lookup.plantation?.plantationId
    ),
    preferredLanguageCode: firstDefined(
      form.preferredLanguageCode,
      registration?.preferredLanguageCode
    ),
    preferredLanguageLabel: firstDefined(
      form.preferredLanguageLabel,
      registration?.preferredLanguageLabel
    ),
    plantationName: firstDefined(
      form.plantationName,
      registration?.plantationName,
      lookup.plantation?.plantationName
    ),
    plateNumber: firstDefined(form.plateNumber, vehicle?.plateNumber, registration?.plateNumber),
    responderNotes: firstDefined(
      form.responderNotes,
      medicalProfile?.responderNotes,
      registration?.responderNotes
    ),
    registrantRelationship: firstDefined(
      form.registrantRelationship,
      compliance?.registrantRelationship,
      "self"
    ),
    subjectAgeBand,
    tagType,
    vehicleType: firstDefined(form.vehicleType, vehicle?.type, registration?.vehicleType),
    vin: firstDefined(form.vin, vehicle?.vin, registration?.vin),
    year: numberToString(
      firstDefined<number | string>(
        form.year,
        vehicle?.year,
        registration?.vehicleYear
      )
    ),
    zoneName: firstDefined(
      form.zoneName,
      registration?.zoneName,
      lookup.physicalTag?.zoneName,
      lookup.plantation?.zoneName
    ),
  }
}

export function requiresGuardianApproval(
  form: Pick<RegistrationFormState, "subjectAgeBand">,
  lookup: PublicLookupData | null
) {
  const isPlantations = isPlantationsLookup(lookup)

  return isGuardianRequiredRegistration(
    isPlantations ? null : lookup,
    form.subjectAgeBand
  )
}

export function validateRegistrationForm(
  form: RegistrationFormState,
  phoneVerified: boolean,
  lookup: PublicLookupData | null = null
): string[] {
  const errors: string[] = []
  const isPlantations = isPlantationsLookup(lookup)
  const needsVehicle = form.tagType !== "personal_tag"
  const isFleet = form.tagType === "fleet_tag"
  const guardianRequired = requiresGuardianApproval(form, lookup)

  if (!form.deviceTag.trim()) {
    errors.push("Enter the SafetyQR tag before saving the registration.")
  }

  if (!form.ownerName.trim()) {
    errors.push("Enter the primary contact name.")
  }

  if (!form.ownerPhone.trim()) {
    errors.push("Enter the primary contact phone number.")
  }

  if (!isPlantations) {
    if (!form.registrantRelationship.trim()) {
      errors.push("Choose your relationship to the profile being registered.")
    }

    if (guardianRequired) {
      if (!form.guardianName.trim()) {
        errors.push("Enter the parent or legal guardian name.")
      }

      if (!form.guardianPhone.trim()) {
        errors.push("Enter the parent or legal guardian phone number.")
      }

      if (!form.guardianRelationship.trim()) {
        errors.push("Choose the guardian relationship.")
      }

      if (normalizePhone(form.guardianPhone) === normalizePhone(form.ownerPhone)) {
        errors.push("The guardian phone must be different from the subject or primary contact phone.")
      }
    } else {
      if (!phoneVerified) {
        errors.push("Verify the primary contact phone number before saving this registration.")
      }

      if (!form.noticeVersionId.trim()) {
        errors.push("The current consent notice must be loaded before registration can continue.")
      }

      if (!form.consentAccepted) {
        errors.push("Review and accept the current consent notice before saving this registration.")
      }
    }
  }

  if (isPlantations) {
    if (!form.emergencyContactName.trim()) {
      errors.push("Enter the supervisor name.")
    }

    if (!form.emergencyContactPhone.trim()) {
      errors.push("Enter the supervisor phone number.")
    }

    if (!form.plantationId.trim()) {
      errors.push("Enter the plantation ID.")
    }

    if (!form.zoneName.trim()) {
      errors.push("Enter the zone name for this tag.")
    }

    if (!form.preferredLanguageCode.trim()) {
      errors.push("Choose the local reporting language for this Plantation kit.")
    }

    return errors
  }

  if (isFleet) {
    if (!form.fleetName.trim()) {
      errors.push("Enter the fleet name.")
    }

    if (!form.companyName.trim()) {
      errors.push("Enter the company name.")
    }
  }

  if (needsVehicle) {
    if (!form.vehicleType.trim()) {
      errors.push("Choose a vehicle type.")
    }

    if (!form.make.trim()) {
      errors.push("Enter the vehicle make.")
    }

    if (!form.model.trim()) {
      errors.push("Enter the vehicle model.")
    }

    if (!form.plateNumber.trim()) {
      errors.push("Enter the plate number.")
    }
  }

  return errors
}

export function buildPublicRegistrationPayload(
  form: RegistrationFormState,
  lookup: PublicLookupData | null
): PublicRegistrationRequest {
  const existing = lookup?.registration ?? null
  const isPlantations = isPlantationsLookup(lookup)
  const guardianRequired = requiresGuardianApproval(form, lookup)
  const payloadBase = {
    organizationId: resolveOrganizationId(lookup, existing),
    status: existing?.status ?? "active",
    stickerId: form.deviceTag.trim(),
    tag: form.deviceTag.trim(),
    tagType: form.tagType,
    type: form.tagType,
  }

  if (isPlantations) {
    const preferredLanguageCode = form.preferredLanguageCode.trim()
    const preferredLanguageLabel = (
      form.preferredLanguageLabel.trim()
      || getPlantationLocalLanguageOption(preferredLanguageCode)?.label
      || null
    )

    return {
      ...payloadBase,
      emergencyContactEmail: nullableString(form.emergencyContactEmail),
      emergencyContactName: form.emergencyContactName.trim(),
      emergencyContactPhone: form.emergencyContactPhone.trim(),
      ownerEmail: nullableString(form.ownerEmail),
      ownerName: form.ownerName.trim(),
      ownerPhone: form.ownerPhone.trim(),
      plantationId: form.plantationId.trim(),
      plantationName: nullableString(form.plantationName),
      preferredLanguageCode: preferredLanguageCode || null,
      preferredLanguageLabel,
      zoneName: form.zoneName.trim(),
    }
  }

  if (form.tagType === "personal_tag") {
    return {
      ...payloadBase,
      allergies: nullableString(form.allergies),
      bloodType: nullableString(form.bloodType),
      consentAccepted: form.consentAccepted,
      email: nullableString(form.ownerEmail),
      emergencyContactName: nullableString(form.emergencyContactName),
      emergencyContactPhone: nullableString(form.emergencyContactPhone),
      fullName: form.ownerName.trim(),
      medicalConditions: nullableString(form.medicalConditions),
      medications: nullableString(form.medications),
      noticeVersionId: guardianRequired ? null : nullableString(form.noticeVersionId),
      notes: existing?.notes ?? null,
      phone: form.ownerPhone.trim(),
      responderNotes: nullableString(form.responderNotes),
      registrantRelationship: form.registrantRelationship.trim() || "self",
      subjectAgeBand: guardianRequired ? "under_18" : form.subjectAgeBand,
      ...buildGuardianPayload(form, guardianRequired),
    }
  }

  if (form.tagType === "fleet_tag") {
    const activatedAt = new Date().toISOString()

    return {
      ...payloadBase,
      activatedAt,
      assignedAt: existing?.assignedAt ?? null,
      assigneeEmail: existing?.assigneeEmail ?? null,
      assigneeName: existing?.assigneeName ?? null,
      assigneePhone: existing?.assigneePhone ?? null,
      assignmentStatus: "activated",
      companyName: form.companyName.trim(),
      contactEmail: nullableString(form.ownerEmail),
      contactName: form.ownerName.trim(),
      contactPhone: form.ownerPhone.trim(),
      externalRef: existing?.externalRef ?? null,
      fleetName: form.fleetName.trim(),
      memberType: existing?.memberType ?? null,
      metadata: existing?.metadata ?? null,
      noticeVersionId: guardianRequired ? null : nullableString(form.noticeVersionId),
      notes: existing?.notes ?? null,
      plateNumber: form.plateNumber.trim(),
      registrantRelationship: form.registrantRelationship.trim() || "self",
      subjectAgeBand: guardianRequired ? "under_18" : form.subjectAgeBand,
      tagSource: existing?.tagSource ?? null,
      vehicleColor: nullableString(form.color),
      vehicleMake: form.make.trim(),
      vehicleModel: form.model.trim(),
      vehicleType: form.vehicleType.trim(),
      vehicleYear: parseOptionalNumber(form.year),
      ...buildGuardianPayload(form, guardianRequired),
    }
  }

  return {
    ...payloadBase,
    allergies: nullableString(form.allergies),
    bloodType: nullableString(form.bloodType),
    consentAccepted: form.consentAccepted,
    emergencyContactName: nullableString(form.emergencyContactName),
    emergencyContactPhone: nullableString(form.emergencyContactPhone),
    medicalConditions: nullableString(form.medicalConditions),
    medications: nullableString(form.medications),
    noticeVersionId: guardianRequired ? null : nullableString(form.noticeVersionId),
    ownerEmail: nullableString(form.ownerEmail),
    ownerName: form.ownerName.trim(),
    ownerPhone: form.ownerPhone.trim(),
    plateNumber: form.plateNumber.trim(),
    responderNotes: nullableString(form.responderNotes),
    registrantRelationship: form.registrantRelationship.trim() || "self",
    subjectAgeBand: guardianRequired ? "under_18" : form.subjectAgeBand,
    vehicleColor: nullableString(form.color),
    vehicleMake: form.make.trim(),
    vehicleModel: form.model.trim(),
    vehicleType: form.vehicleType.trim(),
    vehicleYear: parseOptionalNumber(form.year),
    vin: nullableString(form.vin),
    ...buildGuardianPayload(form, guardianRequired),
  }
}

function firstDefined<T>(...values: Array<T | null | undefined | "">): T | "" {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") {
      return value
    }
  }

  return ""
}

function nullableString(value: string): string | null {
  const trimmed = value.trim()
  return trimmed ? trimmed : null
}

function buildGuardianPayload(
  form: RegistrationFormState,
  guardianRequired: boolean
) {
  if (!guardianRequired) {
    return {}
  }

  const guardianEmail = nullableString(form.guardianEmail)
  const guardianName = nullableString(form.guardianName)
  const guardianPhone = nullableString(form.guardianPhone)
  const guardianRelationship = nullableString(form.guardianRelationship)

  return {
    ...(guardianEmail ? { guardianEmail } : {}),
    ...(guardianName ? { guardianName } : {}),
    ...(guardianPhone ? { guardianPhone } : {}),
    ...(guardianRelationship ? { guardianRelationship } : {}),
  }
}

function resolveOrganizationId(
  lookup: PublicLookupData | null,
  existing: RegistrationSummary | null
): string | undefined {
  if (existing?.organizationId) {
    return existing.organizationId
  }

  if (lookup?.device?.organizationId) {
    return lookup.device.organizationId
  }

  return undefined
}

function parseOptionalNumber(value: string): number | null {
  const trimmed = value.trim()

  if (!trimmed) {
    return null
  }

  const parsed = Number.parseInt(trimmed, 10)

  if (!Number.isFinite(parsed)) {
    return null
  }

  return parsed
}

function isPlantationsLookup(lookup: PublicLookupData | null): boolean {
  return lookup?.kit?.productFamily === "site_safety_qr"
    && lookup?.kit?.solutionCategory === "plantations"
}

function normalizePhone(value: string) {
  return value.replace(/\D+/g, "")
}

function numberToString(value: number | string | null | undefined): string {
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value)
  }

  if (typeof value === "string") {
    return value
  }

  return ""
}
